import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { faker } from '@faker-js/faker';
// @mui
import { useTheme } from '@mui/material/styles';
import { Grid, Container, Typography, Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

// components
import Iconify from '../components/iconify';
// sections
import {
  AppTasks,
  AppNewsUpdate,
  AppOrderTimeline,
  AppCurrentVisits,
  AppWebsiteVisits,
  AppTrafficBySite,
  AppWidgetSummary,
  AppCurrentSubject,
  AppConversionRates,
} from '../sections/@dashboard/app';

import getDashboardMonthlyPlanMetrics from '../utils/apis/user/getDashBoardMonthlyPlanMetrics';

// ----------------------------------------------------------------------

export default function Dashboard() {
  const theme = useTheme();

  const [totalUserCount, setTotalUserCount] = useState(248);
  const [DAU, setDAU] = useState(0);
  const [WAU, setWAU] = useState(0);
  const [MAU, setMAU] = useState(0);

  const [freeType, setFreeType] = useState(0);
  const [basicType, setBasicType] = useState(0);
  const [proType, setProType] = useState(0);
  const [entType, setEntType] = useState(0);
  const [countryData, setCountryData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartColors, setChartColors] = useState([]);

  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [monthlyPlanMetrics, setMonthlyPlanMetrics] = useState([]);

  const months = useMemo(() => {
    if (isLgUp) return '12';
    if (isMdUp) return '9';
    return '6';
  }, [isLgUp, isMdUp]);

  useEffect(() => {
    let isMounted = true;

    const fetchMonthlyPlanMetrics = async () => {
      try {
        const res = await getDashboardMonthlyPlanMetrics(months);
        const rows = Array.isArray(res.data) ? res.data : [];

        if (!isMounted) return;
        setMonthlyPlanMetrics(rows);
      } catch (error) {
        console.error('월별 플랜 접속량 조회 실패', error);

        if (!isMounted) return;
        setMonthlyPlanMetrics([]);
      }
    };

    fetchMonthlyPlanMetrics();

    return () => {
      isMounted = false;
    };
  }, [months]);

  const monthlyPlanChartLabels = useMemo(() => {
    return monthlyPlanMetrics.map(item => item.month_key.replace('-', '/'));
  }, [monthlyPlanMetrics]);

  const monthlyPlanChartData = useMemo(() => {
    return [
      {
        name: '플랜 없음',
        type: 'column',
        fill: 'solid',
        data: monthlyPlanMetrics.map(item => item.plans?.NO_PLAN ?? 0),
      },
      {
        name: '무료 플랜',
        type: 'column',
        fill: 'gradient',
        data: monthlyPlanMetrics.map(item => item.plans?.raindrop_free ?? 0),
      },
      {
        name: '베이직 플랜',
        type: 'area',
        fill: 'gradient',
        data: monthlyPlanMetrics.map(item => {
          const basic = item.plans?.raindrop_basic ?? 0;
          const basicNew = item.plans?.raindrop_basic_new ?? 0;
          return basic + basicNew;
        }),
      },
      {
        name: '프로 플랜',
        type: 'line',
        fill: 'solid',
        data: monthlyPlanMetrics.map(item => {
          const pro = item.plans?.raindrop_pro ?? 0;
          const proNew = item.plans?.raindrop_pro_new ?? 0;
          return pro + proNew;
        }),
      },
    ];
  }, [monthlyPlanMetrics]);



  const fetchData = async () => {
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_SERVER_URL}/boards/dashboard/8e`,
        method: 'post',
      });

      // console.log("data",response.data[0][0]);

      setTotalUserCount(response.data[0][0].total_user_count);
      setDAU(response.data[0][0].dau);
      setWAU(response.data[0][0].wau);
      setMAU(response.data[0][0].mau);

      setFreeType(response.data[0][0].FREE_TYPE);
      setBasicType(response.data[0][0].BASIC_TYPE);
      setProType(response.data[0][0].PRO_TYPE);
      setEntType(response.data[0][0].ENT_TYPE);

      // const countryData = countryResponse.data;

      axios
        .post(`${process.env.REACT_APP_SERVER_URL}/boards/dashboardcountry`)
        .then((response) => {
          console.log('countryResponse', response.data);
          setChartData(
            response.data.map((item) => ({
              label: item.full_name,
              value: item.count,
            }))
          );

          // Update chartColors state
          setChartColors(response.data.map(() => getRandomColor()));
        })
        .catch((response) => {
          console.log(response);
        });
    } catch (error) {
      console.log('error', error);
    }
  };

  // const chartData = data.map(item => ({
  //   label: item.label,
  //   value: item.value,
  // }));

  // const chartColors = data.map(() => getRandomColor());

  function getRandomColor() {
    return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
  }
  useLayoutEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <Helmet>
        <title> Dashboard | Minimal UI </title>
      </Helmet>

      <Box style={{ padding: '60px 10%' }}>
        <Typography variant="h3" sx={{ mb: 5, fontWeight: 600 }}>
          다시 오신 것을 환영합니다. 관리자님
        </Typography>
        <p>2023년 03월</p>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary title="전체 유저수" total={totalUserCount} icon={'line-md:account'} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary title="금일 접속 카운트" total={DAU} color="info" icon={'svg-spinners:gooey-balls-1'} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary
              title="주간 접속 카운트"
              total={WAU}
              color="warning"
              icon={'svg-spinners:blocks-shuffle-3'}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary title="월간 접속 카운트" total={MAU} color="error" icon={'eos-icons:atom-electron'} />
          </Grid>

          {/* <Grid item xs={12} md={6} lg={8}>
            <AppWebsiteVisits
              title="신규 가입유저"
              subheader="(+43%) than last year"
              chartLabels={[
                '01/01/2003',
                '02/01/2003',
                '03/01/2003',
                '04/01/2003',
                '05/01/2003',
                '06/01/2003',
                '07/01/2003',
                '08/01/2003',
                '09/01/2003',
                '10/01/2003',
                '11/01/2003',
              ]}
              chartData={[
                {
                  name: 'Team A',
                  type: 'column',
                  fill: 'solid',
                  data: [23, 11, 22, 27, 13, 22, 37, 21, 44, 22, 30],
                },
                {
                  name: 'Team B',
                  type: 'area',
                  fill: 'gradient',
                  data: [44, 55, 41, 67, 22, 43, 21, 41, 56, 27, 43],
                },
                {
                  name: 'Team C',
                  type: 'line',
                  fill: 'solid',
                  data: [30, 25, 36, 30, 45, 35, 64, 52, 59, 36, 39],
                },
              ]}
            />
          </Grid> */}
          <Grid item xs={12} md={6} lg={4}>
            <AppCurrentVisits title="국가별 사용자" chartData={chartData} chartColors={chartColors} />
          </Grid>

          <Grid item xs={12} md={6} lg={8} style={{ display: 'flex', flex: 1 }}>
            <AppConversionRates
              style={{ flex: 1 }}
              title="플랜 사용률"
              subheader=""
              chartData={[
                { label: '무료 플랜 사용자', value: freeType },
                { label: '베이직 플랜 사용자', value: basicType },
                { label: '프로 플랜 사용자', value: proType },
                { label: '엔터프라이즈', value: entType },
              ]}
            />
          </Grid>

          <Grid item xs={12} >
            <AppWebsiteVisits
              title={`최근 ${months}개월 플랜 별 접속량`}
              subheader=""
              chartLabels={monthlyPlanChartLabels}
              chartData={monthlyPlanChartData}
            />
          </Grid>

          {/* <Grid item xs={12} md={6} lg={4} style={{ display: 'flex', flex: 1 }}>
            <AppCurrentSubject
              style={{ flex: 1 }}
              title="계정 타입"
              chartLabels={['접수', '납품', '정산']}
              chartData={[
                { name: '무료 플랜', data: [187, 132, 30] },
                { name: '베이직 플랜', data: [286, 231, 40] },
                { name: '프로 플랜', data: [447, 368, 78] },
              ]}
              chartColors={[...Array(6)].map(() => theme.palette.text.secondary)}
            />
          </Grid> */}

          {/* <Grid item xs={12} md={6} lg={8}>
            <AppNewsUpdate
              title="News Update"
              list={[...Array(5)].map((_, index) => ({
                id: faker.datatype.uuid(),
                title: faker.name.jobTitle(),
                description: faker.name.jobTitle(),
                image: `/assets/images/covers/cover_${index + 1}.jpg`,
                postedAt: faker.date.recent(),
              }))}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AppOrderTimeline
              title="Order Timeline"
              list={[...Array(5)].map((_, index) => ({
                id: faker.datatype.uuid(),
                title: [
                  '1983, orders, $4220',
                  '12 Invoices have been paid',
                  'Order #37745 from September',
                  'New order placed #XF-2356',
                  'New order placed #XF-2346',
                ][index],
                type: `order${index + 1}`,
                time: faker.date.past(),
              }))}
            />
          </Grid> */}
        </Grid>
      </Box>
    </>
  );
}
